import {ICON_TYPE} from "@/components/library/Icon";
import {CHANNEL_TYPE} from "@/enums/ai";
import {
   Assistant,
   AssistantChannel,
   AssistantChatChannel,
   AssistantGptChannel,
   AssistantTask,
   DEFAULT_ASSISTANT_MODEL,
   FileUploadConfiguration,
} from "@/types/ai";
import {LangString} from "@/types/api";
import {
   getModelDisplayName as getCatalogModelDisplayName,
   getModelProvider as getCatalogModelProvider,
   isAutoAssistantModel,
   type ModelProvider,
} from "@/utils/ai/modelCatalog";
import {getDefaultFileCount, getDefaultFileSize, isGeminiAssistant} from "@/utils/fileHelpers";
import {IconName} from "@fortawesome/fontawesome-common-types";

export type {ModelProvider};

export const GetChannelIcon = (type: CHANNEL_TYPE): IconName => {
   switch (type) {
      case CHANNEL_TYPE.CHAT:
         return "comment";
      case CHANNEL_TYPE.GPT:
         return "robot";
      case CHANNEL_TYPE.SMS:
         return "mobile";
      default:
         return "question-circle";
   }
};

export const HasAtLeastOneLanguage = (text: LangString | undefined): boolean => {
   if (!text) {
      return false;
   }

   const languages = Object.values(text);

   return languages.some((lang) => {
      return lang && lang.length > 0;
   });
};

export const ValidateChannelChat = (channels: Assistant["channels"] | undefined): boolean => {
   const chatChannel = (channels ?? []).find((channel) => channel.type === CHANNEL_TYPE.CHAT);

   if (!chatChannel) {
      return true; //We are okay if there is no chat channel
   }

   return HasAtLeastOneLanguage(chatChannel.greeting);
};

export const ValidateChannelGpt = (channels: Assistant["channels"] | undefined): boolean => {
   const gptChannel = (channels ?? []).find((channel) => channel.type === CHANNEL_TYPE.GPT);

   if (!gptChannel) {
      return true; //We are okay if there is no gpt channel
   }

   return HasAtLeastOneLanguage(gptChannel.greeting);
};

export const ValidateChannelSms = (channels: Assistant["channels"] | undefined): boolean => {
   const smsChannel = (channels ?? []).find((channel) => channel.type === CHANNEL_TYPE.SMS);

   if (!smsChannel) {
      return true; //We are okay if there is no sms channel
   }

   return HasAtLeastOneLanguage(smsChannel.greeting);
};

export const ValidateHasAtLeastOneChannel = (
   channels: Assistant["channels"] | undefined
): boolean => {
   return (channels ?? []).length > 0;
};

export const ValidateChannel = (
   channels: Assistant["channels"] | undefined,
   channelType: CHANNEL_TYPE
): boolean => {
   switch (channelType) {
      case CHANNEL_TYPE.CHAT:
         return ValidateChannelChat(channels);
      case CHANNEL_TYPE.GPT:
         return ValidateChannelGpt(channels);
      case CHANNEL_TYPE.SMS:
         return ValidateChannelSms(channels);
      default:
         return true;
   }
};

export const ValidateFileUpload = (
   channels: Assistant["channels"] | undefined,
   channelType: CHANNEL_TYPE
): boolean => {
   const channel = (channels ?? []).find((c) => c.type === channelType);

   if (!channel) {
      return true; //We are okay if there is no channel
   }

   // For other channel types, check if fileUpload is enabled and properly configured
   const channelWithFileUpload = channel as AssistantChatChannel | AssistantGptChannel;
   if (!channelWithFileUpload.fileUpload?.enabled) {
      return true; //We are okay if fileUpload is disabled
   }

   return (
      (channelWithFileUpload.fileUpload.maxFileCount || 0) > 0 &&
      (channelWithFileUpload.fileUpload.totalMaxFileSizeBytes || 0) > 0 &&
      (channelWithFileUpload.fileUpload.allowedMimeTypes?.length || 0) > 0 &&
      (channelWithFileUpload.fileUpload.maxFileSizeBytes || 0) > 0
   );
};

export const ValidateTask = (task: AssistantTask): number => {
   // TODO: GAIL-1448 This is to support existing tasks that are singular
   if (!Array.isArray(task.instructions)) {
      return (
         (task.name ? 0 : 1) +
         (task.description ? 0 : 1) +
         ((task.instructions.type === "use_case" &&
            task.instructions.useCaseAssociations &&
            task.instructions.useCaseAssociations.length > 0) ||
         (task.instructions.type === "custom" && task.instructions.instructions.length > 0) ||
         (task.instructions.type === "system_task" && task.instructions.systemTaskId.length > 0)
            ? 0
            : 1)
      );
   }

   return (
      (task.name ? 0 : 1) +
      (task.description ? 0 : 1) +
      (task.instructions.some((instr) => {
         if (
            (instr.type === "use_case" &&
               instr.useCaseAssociations &&
               instr.useCaseAssociations.length > 0) ||
            (instr.type === "custom" && instr.instructions && instr.instructions.length > 0) ||
            (instr.type === "system_task" && instr.systemTaskId && instr.systemTaskId.length > 0)
         ) {
            return true;
         }
         return false;
      })
         ? 0
         : 1)
   );
};

export const ValidateTasks = (tasks: AssistantTask[] | undefined): number => {
   if (!tasks || tasks.length === 0) {
      return 1;
   }

   return tasks.reduce((acc, task) => acc + ValidateTask(task), 0);
};

// Returns true if all fields are filled, false otherwise
const isFieldComplete = (values: (string | boolean | undefined | any[])[]): boolean => {
   const emptyFields = values.filter((value) => {
      if (Array.isArray(value)) {
         return value.length === 0;
      }
      return !value;
   });
   return emptyFields.length === 0;
};

export const IsAssistantValid = (assistant: Assistant | null): boolean => {
   if (!assistant) {
      return false;
   }

   const isValid = isFieldComplete([
      assistant.name,
      assistant.description,
      assistant.persona?.name,
      assistant.persona?.voiceId,
      ValidateHasAtLeastOneChannel(assistant.channels),
      ValidateChannelChat(assistant.channels),
      ValidateChannelGpt(assistant.channels),
      ValidateChannelSms(assistant.channels),
      ValidateTasks(assistant.tasks) === 0,
   ]);

   return isValid;
};

export const DEFAULT_FILE_UPLOAD: FileUploadConfiguration = {
   enabled: false,
   allowedMimeTypes: [],
   maxFileSizeBytes: getDefaultFileSize(),
   maxFileCount: getDefaultFileCount(),
   totalMaxFileSizeBytes: getDefaultFileSize(),
};

/**
 * Get the tooltip key for a model based on its type
 * @param model - The model string (e.g., "gpt-4.1-mini", "claude-sonnet-4-0")
 * @returns The tooltip key for the model
 */
// TODO: This will be removed soon as we just expose the models themselves now
export const getModelTooltipKey = (model: string): string => {
   if (model === "gpt-4.1-mini") {
      return "speed";
   } else if (model === "gpt-4.1") {
      return "general";
   } else if (model === "claude-sonnet-4-0") {
      return "analytical";
   } else if (isGeminiAssistant(model)) {
      // Doing this to handle existing 2.5 pro assistants, while we migrate to 2.5 flash
      return "gemini";
   } else {
      return "general";
   }
};

export const getModelProvider = (model: string): ModelProvider => {
   return getCatalogModelProvider(model);
};

/**
 * Get an icon for a model's provider
 */
export const getModelIcon = (model: string): IconName | "custom-anthropic" => {
   if (isAutoAssistantModel(model)) {
      return "sparkles";
   }

   const provider = getModelProvider(model);
   switch (provider) {
      case "openai":
         return "openai";
      case "anthropic":
         return "custom-anthropic";
      case "google":
         return "google";
      default:
         return "question";
   }
};

/** Sparkles lives in the light sprite, not brands. */
export const getModelIconType = (model: string): ICON_TYPE =>
   isAutoAssistantModel(model) ? ICON_TYPE.LIGHT : ICON_TYPE.BRAND;

/**
 * Get the display name for a model
 * @param model - The model identifier (e.g., "gpt-5.2-pro", "claude-opus-4-5")
 * @returns The catalog display name, or the raw alias when the catalog has no row
 */
export const getModelDisplayName = (model: string): string => {
   return getCatalogModelDisplayName(model);
};

/**
 * Resolve the model for the GPT channel, falling back to DEFAULT_ASSISTANT_MODEL.
 */
export const resolveGptModel = (channels: AssistantChannel[] | undefined): string => {
   const gpt = (channels || []).find((c) => c.type === CHANNEL_TYPE.GPT);
   return gpt?.model || DEFAULT_ASSISTANT_MODEL;
};
